<template>
    <div>
        <!-- Create Modal -->
        <edit-modal
        v-if="modalParams.type === 'create'"
        :modalParams="createModalParams"
        :requestPayload="getCreateRequestPayload"
        @hideMe="emit('hideMe', $event)"
        @applyMandatoryFieldError="applyMandatoryFieldCreateErrorHandler"
        @requestSuccessful="emit('requestSuccessful', $event)"
        @input="[createModalParams.content = $event, createModalParams.errorState = false, createModalParams.errorMessage = '']"/>
        <!-- Edit Modal -->
        <edit-modal
        v-if="modalParams.type === 'edit'"
        :modalParams="editModalParams"
        :requestPayload="getEditRequestPayload"
        @hideMe="emit('hideMe', $event)"
        @applyMandatoryFieldError="applyMandatoryFieldEditErrorHandler"
        @requestSuccessful="emit('requestSuccessful', $event)"
        @input="[editModalParams.content = $event, editModalParams.errorState = false, editModalParams.errorMessage = '']"/>
        <!-- Remove Modal -->
        <remove-modal
        v-if="modalParams.type === 'remove'"
        negative
        rightButtonIcon="check-line"
        titleIcon="delete"
        :title="t('favorites.removeModal.title')"
        :text="t('favorites.removeModal.text')"
        :rightButtonText="t('favorites.removeModal.rightButtonText')"
        :leftButtonText="t('favorites.removeModal.leftButtonText')"
        @hideMe="emit('hideMe')"
        @rightButtonClicked="removeRequestHandler"/>
        <!-- Loader -->
        <loader-generic v-if="showLoader" isOverlay />
    </div>
</template>

<script setup>
import EditModal from '@/components/generic/modals/centered/EditInputModal'
import RemoveModal from '@/components/generic/modals/centered/Generic'
import LoaderGeneric from '@/components/generic/loaders/Generic'

/* Vue */
import { ref, computed } from 'vue'

/* Store */
import store from '@/store/store'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['hideMe', 'requestSuccessful', 'requestError'])

/* props received
@modalParams {Object}
    - id {Number, String}
    - operationId {Number, String}
    - type {String}
    - alias {String}
    - orderNumber {Number}
@rightButtonIcon {String} - icon for right button
*/
const props = defineProps({
    modalParams: Object,
    rightButtonIcon: String
})

const showLoader = ref(false)

/* *** CREATE MODAL *** */
const createModalParams = ref({
    labelText: t('favorites.createModal.label'),
    placeholder: t('favorites.createModal.placeholder'),
    title: t('favorites.createModal.title'),
    titleIcon: 'star',
    content: '',
    showCounter: true,
    maxLength: 25,
    mandatory: true,
    editAction: 'favorites/createNewFavorite',
    errorState: false,
    errorMessage: null
})

const getCreateRequestPayload = computed(() => {
    return {
        id: props.modalParams.id,
        operationId: props.modalParams.operationId,
        orderNumber: props.modalParams.orderNumber,
        alias: createModalParams.value.content
    }
})

const applyMandatoryFieldCreateErrorHandler = () => {
    createModalParams.value = {
        ...createModalParams.value,
        errorState: true,
        errorMessage: t('mandatoryFieldError')
    }
}

/* *** EDIT MODAL *** */
const editModalParams = ref({
    labelText: t('favorites.editModal.label'),
    placeholder: t('favorites.createModal.placeholder'),
    title: t('favorites.editModal.title'),
    titleIcon: 'star',
    content: props.modalParams.alias,
    showCounter: true,
    maxLength: 25,
    mandatory: true,
    editAction: 'favorites/editFavoriteName',
    errorState: false,
    errorMessage: null
})

const getEditRequestPayload = computed(() => {
    return {
        id: props.modalParams.id,
        alias: editModalParams.value.content
    }
})

const applyMandatoryFieldEditErrorHandler = () => {
    editModalParams.value = {
        ...editModalParams.value,
        errorState: true,
        errorMessage: t('mandatoryFieldError')
    }
}

/*************************************************
*                                                *
*                   Request Logic                *
*                                                *
*************************************************/
const removeRequestHandler = async () => {
    showLoader.value = true

    try {
        const res = await store.dispatch('favorites/deleteFavorite', props.modalParams.operationId)
        emit('requestSuccessful', res.data)
        showLoader.value = false
    } catch (error) {
        emit('requestError', error.response.data)
        showLoader.value = false
    }
}
</script>

<style lang="scss" scoped>

</style>
